import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, ChevronDown, ChevronRight, FileSpreadsheet, Plus, Save, Trash2, Upload, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RequirePermission from '../../../../components/AccessControl/RequirePermission';
import { useStore } from '../../../../context/StoreContext';
import {
    loadServices, saveServicesBulk, errorText, toNumber, clean, serviceKey, effectivePrice, newRowId,
    readColumn, discountTypeOf, isYes, readSheetFile, writeSheet, writeCsv,
} from '../../pricelistUtils';
import styles from './PricelistMaster.module.css';

// =============================================================================
// Master price list: every section (Men, Women, Kids...), service and add-on in one grid.
// Excel / CSV: one row per add-on; rows of the same Audience + Category + Service merge.
// Saving sends everything in one request; services not in the grid are left as they are.
// =============================================================================

const addonRow = (a = {}) => ({ id: a._id || a.id || newRowId('addon'), addonId: a._id || a.id || '', name: a.name || '', duration: a.duration ?? '', price: a.price ?? '' });
const serviceRow = (s = {}) => {
    const d = s.discount || {};
    return {
        id: s.id || s._id || newRowId('service'),
        serviceId: s.id || s._id || '',
        name: s.name || '', demographic: s.targetDemographic || '', category: s.category || '',
        duration: s.duration ?? '', price: s.price ?? '',
        discountActive: Boolean(d.isActive), discountType: d.type || 'Percentage', discountValue: d.isActive ? (d.value ?? '') : '',
        addons: Array.isArray(s.addons) ? s.addons.map(addonRow) : [],
    };
};
const keyOfRow = (r) => serviceKey(r.demographic, r.category, r.name);

/** Excel rows -> grid rows. Matches existing services by Audience + Category + Name. */
const rowsFromSheet = (sheetRows, existing) => {
    const existingByKey = new Map(existing.map((s) => [serviceKey(s.targetDemographic, s.category, s.name), s]));
    const byKey = new Map();
    let skipped = 0;
    sheetRows.forEach((src) => {
        const name = clean(readColumn(src, ['service name', 'service', 'name']));
        const demographic = clean(readColumn(src, ['audience', 'target demographic', 'demographic', 'gender', 'section', 'for', 'customer type']));
        const category = clean(readColumn(src, ['category', 'service category']));
        if (!name) { skipped += Object.values(src).some((v) => String(v).trim()) ? 1 : 0; return; }
        const k = serviceKey(demographic, category, name);
        const discountValue = readColumn(src, ['discount value', 'discount amount', 'offer value', 'offer']);
        const discountActive = isYes(readColumn(src, ['discount active', 'offer active'])) || String(discountValue).trim() !== '';
        const addonName = clean(readColumn(src, ['add-on name', 'addon name', 'add on name', 'add-on', 'addon']));

        if (!byKey.has(k)) {
            const ex = existingByKey.get(k);
            byKey.set(k, {
                ...serviceRow(ex || {}),
                id: ex?.id || newRowId('import'),
                serviceId: ex?.id || '',
                name, demographic, category,
                duration: Math.max(0, toNumber(readColumn(src, ['service duration', 'duration', 'duration mins', 'minutes', 'mins']), 0)) || ex?.duration || '',
                price: (() => { const p = readColumn(src, ['base price', 'service price', 'price', 'mrp', 'rate', 'amount']); return String(p).trim() === '' ? (ex?.price ?? '') : Math.max(0, toNumber(p)); })(),
                discountActive,
                discountType: discountTypeOf(readColumn(src, ['discount type', 'offer type'])),
                discountValue: discountActive ? Math.max(0, toNumber(discountValue)) : '',
                addons: [],
            });
        }
        if (addonName) {
            const row = byKey.get(k);
            if (!row.addons.some((a) => clean(a.name).toLowerCase() === addonName.toLowerCase())) {
                const exAddon = (existingByKey.get(k)?.addons || []).find((a) => clean(a.name).toLowerCase() === addonName.toLowerCase());
                row.addons.push({
                    ...addonRow(exAddon || {}),
                    name: addonName,
                    duration: Math.max(0, toNumber(readColumn(src, ['add-on duration', 'addon duration', 'add on duration', 'add-on duration mins', 'addon duration mins']), 0)),
                    price: Math.max(0, toNumber(readColumn(src, ['add-on price', 'addon price', 'add on price', 'addon amount']), 0)),
                });
            }
        }
    });
    // a service listed without add-on rows keeps the add-ons it already has
    const rows = [...byKey.values()].map((r) => (r.addons.length ? r : { ...r, addons: (existingByKey.get(keyOfRow(r))?.addons || []).map(addonRow) }));
    return { rows, skipped };
};

const flatten = (rows) => rows.filter((s) => clean(s.name)).flatMap((s) => {
    const addons = s.addons.filter((a) => clean(a.name));
    return (addons.length ? addons : [null]).map((a) => ({
        Audience: clean(s.demographic), Category: clean(s.category), 'Service Name': clean(s.name),
        'Service Duration (mins)': toNumber(s.duration), 'Base Price': toNumber(s.price),
        'Discount Type': s.discountActive ? s.discountType : '', 'Discount Value': s.discountActive ? toNumber(s.discountValue) : '',
        'Effective Price': effectivePrice(s),
        'Add-on Name': a ? clean(a.name) : '', 'Add-on Duration (mins)': a ? toNumber(a.duration) : '', 'Add-on Price': a ? toNumber(a.price) : '',
    }));
});

const PricelistMaster = () => {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const fileInputRef = useRef(null);
    const storeId = activeStore?.id || activeStore?._id;
    const [services, setServices] = useState([]);
    const [rows, setRows] = useState([serviceRow()]);
    const [expanded, setExpanded] = useState(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [status, setStatus] = useState({ type: '', text: '', details: [] });
    const [dirty, setDirty] = useState(false);

    const validRows = rows.filter((r) => clean(r.name));
    const addonCount = validRows.reduce((n, r) => n + r.addons.filter((a) => clean(a.name)).length, 0);
    const audiences = useMemo(() => [...new Set([...services.map((s) => s.targetDemographic), ...rows.map((r) => clean(r.demographic))].filter(Boolean))], [services, rows]);
    const categories = useMemo(() => [...new Set([...services.map((s) => s.category), ...rows.map((r) => clean(r.category))].filter(Boolean))], [services, rows]);
    const sections = useMemo(() => [...new Set(validRows.map((r) => clean(r.demographic)).filter(Boolean))], [validRows]);

    const fetchServices = useCallback(async () => {
        if (!storeId || storeId === 'global_hq') { setIsLoading(false); return; }
        setIsLoading(true);
        try {
            const data = await loadServices(storeId);
            setServices(data);
            setRows(data.length ? data.map(serviceRow) : [serviceRow()]);
            setExpanded(new Set());
            setDirty(false);
        } catch (e) { setStatus({ type: 'error', text: errorText(e), details: [] }); } finally { setIsLoading(false); }
    }, [storeId]);
    useEffect(() => { fetchServices(); }, [fetchServices]);

    // warn before leaving with unsaved changes
    useEffect(() => {
        if (!dirty) return undefined;
        const h = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', h);
        return () => window.removeEventListener('beforeunload', h);
    }, [dirty]);

    const edit = (fn) => { setRows(fn); setDirty(true); };
    const setField = (id, field, value) => edit((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    const setOffer = (id, value) => edit((rs) => rs.map((r) => (r.id === id ? { ...r, discountActive: value !== 'None', discountType: value === 'None' ? 'Percentage' : value, discountValue: value === 'None' ? '' : r.discountValue } : r)));
    const setAddon = (sid, aid, field, value) => edit((rs) => rs.map((r) => (r.id === sid ? { ...r, addons: r.addons.map((a) => (a.id === aid ? { ...a, [field]: value } : a)) } : r)));
    const toggle = (id) => setExpanded((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const addService = () => { const r = serviceRow(); edit((rs) => [...rs, r]); };
    const removeService = (id) => edit((rs) => { const n = rs.filter((r) => r.id !== id); return n.length ? n : [serviceRow()]; });
    const addAddon = (sid) => { edit((rs) => rs.map((r) => (r.id === sid ? { ...r, addons: [...r.addons, addonRow()] } : r))); setExpanded((c) => new Set([...c, sid])); };
    const removeAddon = (sid, aid) => edit((rs) => rs.map((r) => (r.id === sid ? { ...r, addons: r.addons.filter((a) => a.id !== aid) } : r)));

    /* ---------------- import / export ---------------- */
    const importFile = async (file) => {
        if (!file) return;
        setIsImporting(true);
        try {
            const { rows: imported, skipped } = rowsFromSheet(await readSheetFile(file), services);
            if (!imported.length) throw new Error('No services found. The file needs at least Audience, Category, Service Name, Duration and Base Price columns.');
            setRows(imported);
            setExpanded(new Set());
            setDirty(true);
            const secs = [...new Set(imported.map((r) => r.demographic).filter(Boolean))];
            const updates = imported.filter((r) => r.serviceId).length;
            setStatus({
                type: 'success',
                text: `${imported.length} services loaded (${imported.length - updates} new, ${updates} already in the list) in ${secs.length} ${secs.length === 1 ? 'section' : 'sections'}: ${secs.join(', ') || 'no audience set'}. Check the grid, then Save.${skipped ? ` ${skipped} rows had no service name and were skipped.` : ''}`,
                details: [],
            });
        } catch (e) { setStatus({ type: 'error', text: errorText(e), details: [] }); } finally { setIsImporting(false); }
    };

    const template = () => writeSheet([
        { Audience: 'Men', Category: 'Hair', 'Service Name': 'Haircut', 'Service Duration (mins)': 30, 'Base Price': 300, 'Discount Type': '', 'Discount Value': '', 'Add-on Name': 'Hair Wash', 'Add-on Duration (mins)': 10, 'Add-on Price': 80 },
        { Audience: 'Men', Category: 'Hair', 'Service Name': 'Haircut', 'Service Duration (mins)': 30, 'Base Price': 300, 'Discount Type': '', 'Discount Value': '', 'Add-on Name': 'Head Massage', 'Add-on Duration (mins)': 15, 'Add-on Price': 150 },
        { Audience: 'Women', Category: 'Hair', 'Service Name': 'Haircut', 'Service Duration (mins)': 45, 'Base Price': 600, 'Discount Type': 'Percentage', 'Discount Value': 10, 'Add-on Name': '', 'Add-on Duration (mins)': '', 'Add-on Price': '' },
        { Audience: 'Women', Category: 'Skin', 'Service Name': 'Facial', 'Service Duration (mins)': 60, 'Base Price': 1500, 'Discount Type': '', 'Discount Value': '', 'Add-on Name': '', 'Add-on Duration (mins)': '', 'Add-on Price': '' },
        { Audience: 'Kids', Category: 'Hair', 'Service Name': 'Haircut', 'Service Duration (mins)': 20, 'Base Price': 200, 'Discount Type': 'Flat', 'Discount Value': 50, 'Add-on Name': '', 'Add-on Duration (mins)': '', 'Add-on Price': '' },
    ], 'Price_List_Template.xlsx', 'Price list');

    /* ---------------- save ---------------- */
    const validate = () => {
        const problems = [];
        const seen = new Map();
        validRows.forEach((r, i) => {
            const n = i + 1;
            if (!clean(r.demographic)) problems.push(`Row ${n} (${r.name}): audience is missing.`);
            if (!clean(r.category)) problems.push(`Row ${n} (${r.name}): category is missing.`);
            if (r.duration === '' || toNumber(r.duration) < 1) problems.push(`Row ${n} (${r.name}): duration must be at least 1 minute.`);
            if (r.price === '' || toNumber(r.price) < 0) problems.push(`Row ${n} (${r.name}): base price is missing.`);
            if (r.discountActive && r.discountType === 'Percentage' && toNumber(r.discountValue) > 100) problems.push(`Row ${n} (${r.name}): % offer cannot be more than 100.`);
            r.addons.forEach((a) => { if (clean(a.name) && a.price === '') problems.push(`Row ${n} (${r.name}): add-on "${a.name}" needs a price.`); });
            const k = keyOfRow(r);
            if (seen.has(k)) problems.push(`Row ${n}: "${r.name}" is already in row ${seen.get(k)} for ${r.demographic} › ${r.category}.`);
            else seen.set(k, n);
        });
        if (problems.length) { setStatus({ type: 'error', text: `Fix ${problems.length} ${problems.length === 1 ? 'thing' : 'things'} before saving:`, details: problems.slice(0, 12) }); return false; }
        return true;
    };

    const save = async () => {
        if (!storeId || !validRows.length || !validate()) return;
        setIsSaving(true);
        setStatus({ type: '', text: '', details: [] });
        try {
            const payload = validRows.map((r, i) => ({
                rowNo: i + 1,
                ...(r.serviceId ? { id: r.serviceId } : {}),
                name: clean(r.name), targetDemographic: clean(r.demographic), category: clean(r.category),
                duration: Math.max(1, Math.round(toNumber(r.duration))), price: Math.max(0, toNumber(r.price)),
                discount: { isActive: Boolean(r.discountActive) && toNumber(r.discountValue) > 0, type: r.discountType, value: r.discountActive ? Math.max(0, toNumber(r.discountValue)) : 0 },
                addons: r.addons.filter((a) => clean(a.name)).map((a) => ({ ...(a.addonId ? { id: a.addonId } : {}), name: clean(a.name), duration: Math.max(0, Math.round(toNumber(a.duration))), price: Math.max(0, toNumber(a.price)) })),
            }));
            const json = await saveServicesBulk(storeId, payload);
            const failed = (json.data?.results || []).filter((x) => x.status === 'failed');
            if (failed.length) {
                setStatus({ type: json.success ? 'warn' : 'error', text: json.message, details: failed.slice(0, 15).map((f) => `Row ${f.rowNo} (${f.where ? `${f.where} › ` : ''}${f.name}): ${f.message}`) });
            } else {
                setStatus({ type: 'success', text: json.message, details: [] });
            }
            if (json.success) { await fetchServices(); if (failed.length) setDirty(false); }
        } catch (e) { setStatus({ type: 'error', text: errorText(e), details: [] }); } finally { setIsSaving(false); }
    };

    return (
        <div className={styles.pageWrapper}>
            <header className={styles.pageHeader}>
                <div className={styles.headerIdentity}>
                    <button type="button" className={styles.backBtn} onClick={() => (!dirty || window.confirm('Leave without saving?')) && navigate('/pricelist')} aria-label="Back to price list"><ArrowLeft size={16} /></button>
                    <div>
                        <h1 className={styles.pageTitle}>Master price list</h1>
                        <p className={styles.pageSubtitle}>All sections together: Men, Women, Kids... Same service name in two sections stays two services.</p>
                    </div>
                </div>
                <div className={styles.headerRight}>
                    <span className={styles.headerSummary}>{validRows.length} services · {addonCount} add-ons · {sections.length} sections</span>
                    <button type="button" className={styles.cancelBtn} onClick={() => (!dirty || window.confirm('Leave without saving?')) && navigate('/pricelist')}><X size={13} /> Cancel</button>
                    <RequirePermission any={['pricelist.edit', 'pricelist.create', 'pricelist.import']}>
                        <button type="button" className={styles.saveBtn} onClick={save} disabled={!storeId || isSaving || !validRows.length}>
                            <Save size={13} /> {isSaving ? 'Saving...' : `Save ${validRows.length} services`}
                        </button>
                    </RequirePermission>
                </div>
            </header>

            {status.text && (
                <div className={`${styles.statusBar} ${status.type === 'error' ? styles.statusError : styles.statusSuccess}`} style={status.type === 'warn' ? { background: '#fff6e0', color: '#7a4a10', borderColor: '#ffe399' } : undefined}>
                    {status.text}
                    {status.details.length > 0 && <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{status.details.map((d) => <li key={d}>{d}</li>)}</ul>}
                </div>
            )}

            <main className={styles.workspace}>
                <section className={styles.gridPanel}>
                    <div className={styles.panelHeader}>
                        <strong>Review &amp; edit {sections.length > 0 && <small style={{ fontWeight: 700, opacity: 0.7 }}>· {sections.join(' · ')}</small>}</strong>
                        <RequirePermission module="pricelist" action="create">
                            <button type="button" className={styles.addRowBtn} onClick={addService} disabled={!storeId}><Plus size={13} /> Add service</button>
                        </RequirePermission>
                    </div>

                    <div className={styles.tableScroll}>
                        <table className={styles.serviceTable}>
                            <thead>
                                <tr>
                                    <th className={styles.numberCol}>#</th>
                                    <th className={styles.serviceNameCol}>Service name<span>*</span></th>
                                    <th className={styles.demographicCol}>Audience<span>*</span></th>
                                    <th className={styles.categoryCol}>Category<span>*</span></th>
                                    <th className={styles.durationCol}>Duration <small>(mins)</small><span>*</span></th>
                                    <th className={styles.priceCol}>Base price<span>*</span></th>
                                    <th className={styles.discountCol}>Offer</th>
                                    <th className={styles.effectiveCol}>Final price</th>
                                    <th className={styles.actionCol} />
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="9" className={styles.tableMessage}>Loading price list...</td></tr>
                                ) : rows.map((row, index) => {
                                    const open = expanded.has(row.id);
                                    const liveAddons = row.addons.filter((a) => clean(a.name)).length;
                                    return (
                                        <React.Fragment key={row.id}>
                                            <tr className={open ? styles.serviceRowExpanded : ''}>
                                                <td className={styles.rowNumber}>{index + 1}</td>
                                                <td>
                                                    <div className={styles.serviceNameEditor}>
                                                        <button type="button" className={styles.expandBtn} onClick={() => toggle(row.id)} aria-label={open ? 'Hide add-ons' : 'Show add-ons'} title={`${liveAddons} add-ons`}>
                                                            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                        </button>
                                                        <input className={styles.cellInput} value={row.name} onChange={(e) => setField(row.id, 'name', e.target.value)} placeholder="Required" autoComplete="off" />
                                                        {liveAddons > 0 && !open && <small style={{ whiteSpace: 'nowrap', fontWeight: 800, opacity: 0.6 }}>+{liveAddons}</small>}
                                                    </div>
                                                </td>
                                                <td><input className={styles.cellInput} list="master-audiences" value={row.demographic} onChange={(e) => setField(row.id, 'demographic', e.target.value)} placeholder="Men / Women / Kids" autoComplete="off" /></td>
                                                <td><input className={styles.cellInput} list="master-categories" value={row.category} onChange={(e) => setField(row.id, 'category', e.target.value)} placeholder="Category" autoComplete="off" /></td>
                                                <td><input className={`${styles.cellInput} ${styles.numericInput}`} type="number" min="1" value={row.duration} onChange={(e) => setField(row.id, 'duration', e.target.value)} placeholder="0" /></td>
                                                <td><div className={styles.currencyInput}><span>₹</span><input className={`${styles.cellInput} ${styles.numericInput}`} type="number" min="0" value={row.price} onChange={(e) => setField(row.id, 'price', e.target.value)} placeholder="0" /></div></td>
                                                <td className={styles.discountCell}>
                                                    <div className={styles.discountEditor}>
                                                        <select value={row.discountActive ? row.discountType : 'None'} onChange={(e) => setOffer(row.id, e.target.value)} aria-label="Offer type">
                                                            <option value="None">No offer</option><option value="Percentage">% off</option><option value="Flat">₹ off</option>
                                                        </select>
                                                        {row.discountActive && <input type="number" min="0" value={row.discountValue} onChange={(e) => setField(row.id, 'discountValue', e.target.value)} placeholder="0" aria-label="Offer value" />}
                                                    </div>
                                                </td>
                                                <td className={styles.effectiveValue}>₹{effectivePrice(row).toFixed(2)}</td>
                                                <td className={styles.actionCell}><button type="button" className={styles.deleteBtn} onClick={() => removeService(row.id)} title="Remove from the grid (does not delete a saved service)"><Trash2 size={13} /></button></td>
                                            </tr>
                                            {open && (
                                                <tr className={styles.addonSectionRow}>
                                                    <td colSpan="9">
                                                        <div className={styles.addonSection}>
                                                            <div className={styles.addonSectionHeader}>
                                                                <div><strong>Add-ons</strong><span>{liveAddons} attached</span></div>
                                                                <RequirePermission module="pricelist" action="manage_addons">
                                                                    <button type="button" className={styles.addAddonBtn} onClick={() => addAddon(row.id)}><Plus size={12} /> Add add-on</button>
                                                                </RequirePermission>
                                                            </div>
                                                            {row.addons.length ? (
                                                                <table className={styles.addonTable}>
                                                                    <thead><tr><th>Add-on name</th><th>Duration <small>(mins)</small></th><th>Price</th><th>Combo price</th><th /></tr></thead>
                                                                    <tbody>{row.addons.map((a) => (
                                                                        <tr key={a.id}>
                                                                            <td><input className={styles.addonInput} value={a.name} onChange={(e) => setAddon(row.id, a.id, 'name', e.target.value)} placeholder="Add-on name" autoComplete="off" /></td>
                                                                            <td><input className={`${styles.addonInput} ${styles.numericInput}`} type="number" min="0" value={a.duration} onChange={(e) => setAddon(row.id, a.id, 'duration', e.target.value)} placeholder="0" /></td>
                                                                            <td><div className={styles.currencyInput}><span>₹</span><input className={`${styles.addonInput} ${styles.numericInput} ${styles.addonCurrencyInput}`} type="number" min="0" value={a.price} onChange={(e) => setAddon(row.id, a.id, 'price', e.target.value)} placeholder="0" /></div></td>
                                                                            <td className={styles.comboValue}>₹{(effectivePrice(row) + toNumber(a.price)).toFixed(2)}</td>
                                                                            <td className={styles.addonActionCell}><button type="button" className={styles.deleteBtn} onClick={() => removeAddon(row.id, a.id)} title="Remove add-on"><Trash2 size={12} /></button></td>
                                                                        </tr>
                                                                    ))}</tbody>
                                                                </table>
                                                            ) : <div className={styles.emptyAddon}>No add-ons. Use “Add add-on”.</div>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className={styles.gridFooter}><span>{validRows.length} services · {addonCount} add-ons ready to save</span><span>{dirty ? 'Unsaved changes' : 'All saved'}</span></div>
                </section>

                <aside className={styles.uploadPanel}>
                    <h2>Excel / CSV upload</h2>
                    <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; importFile(f); }} hidden />
                    <RequirePermission module="pricelist" action="import" fallback={<div className={styles.warningBox}><AlertCircle size={15} /><p>Ask the owner for "Import price list" access to upload a file.</p></div>}>
                        <button type="button" className={styles.dropZone} onClick={() => fileInputRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); importFile(e.dataTransfer.files?.[0]); }} disabled={!storeId || isImporting}>
                            <Upload size={27} />
                            <strong>{isImporting ? 'Reading file...' : 'Upload the whole menu'}</strong>
                            <span>Men, Women, Kids in one file. One row per add-on.</span>
                        </button>
                    </RequirePermission>

                    <button type="button" className={styles.sampleCard} onClick={template}>
                        <FileSpreadsheet size={16} />
                        <span><strong>Need the format?</strong><small>Download sample with Men, Women and Kids</small></span>
                    </button>

                    <div className={styles.warningBox}>
                        <AlertCircle size={15} />
                        <p>The file fills the grid; nothing is saved until you press Save. A service is matched by Audience + Category + Name: existing ones are updated, new ones added, and services not in the file stay as they are.</p>
                    </div>

                    <RequirePermission module="pricelist" action="export">
                        <div className={styles.uploadBottom}>
                            <span>Download the grid</span>
                            <div>
                                <button type="button" onClick={() => writeCsv(flatten(rows), 'Price_List.csv')} disabled={!validRows.length}>CSV</button>
                                <button type="button" onClick={() => writeSheet(flatten(rows), 'Price_List.xlsx', 'Price list')} disabled={!validRows.length}>Excel</button>
                            </div>
                        </div>
                    </RequirePermission>
                </aside>
            </main>

            <datalist id="master-audiences">{audiences.map((v) => <option key={v} value={v} />)}</datalist>
            <datalist id="master-categories">{categories.map((v) => <option key={v} value={v} />)}</datalist>
        </div>
    );
};

export default PricelistMaster;
